import { useEffect, useMemo } from 'react';

import { nanoid } from 'nanoid';
import { useSearchParams } from 'react-router';

import type { StarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import type { FullPostType } from 'src/app/pages/starterDashboardV2/shared/components';
import type { PlatformDataMap } from 'src/app/pages/starterDashboardV2/shared/hooks';
import type { ReadyToSharePostWithPlatform } from 'src/app/shared/api/readyToShare';

import { hasValidContent } from '../utils/contentValidation';
import type { PlatformGroupedPosts, PlatformType, RecommendationsData } from '../utils/platformGrouping';
import {
	ALL_AVAILABLE_PLATFORMS,
	computeAvailablePlatforms,
	getFullPostType,
	groupLoadingStatesByPlatform,
	groupPostsByPlatformData,
	groupRetryStatesByPlatform,
	hasRequiredGroups,
} from '../utils/platformGrouping';

export type DisplayablePost = {
	fullPostType: FullPostType;
	post?: ReadyToSharePostWithPlatform; // if undefined, indicates a skeleton or retry state
	needsRetry?: boolean; // if true, indicates the post failed to load and can be retried
	key: string; // unique identifier for React keys
};

const platformFullPostTypeMapping: Record<PlatformType, FullPostType[]> = {
	facebook: ['facebook_post', 'facebook_story'],
	instagram: ['instagram_post', 'instagram_story'],
	reddit: ['reddit_post', 'reddit_group_post'],
	facebookgroups: ['facebook_group_post'],
};

const fullPostTypeWeights: Record<FullPostType, number> = {
	facebook_post: 100,
	reddit_group_post: 90,
	reddit_post: 90,
	facebook_group_post: 80,
	instagram_story: 70,
	facebook_story: 60,
	instagram_post: 50,
};

const mobileFullPostTypeWeights: Record<FullPostType, number> = {
	facebook_post: 100,
	instagram_story: 90,
	reddit_group_post: 80,
	reddit_post: 80,
	facebook_group_post: 70,
	facebook_story: 60,
	instagram_post: 50,
};

function addSkeletonsForPlatform(platform: PlatformType, posts: DisplayablePost[]) {
	platformFullPostTypeMapping[platform].forEach((fullPostType) => {
		// Skip reddit_post - only show reddit_group_post for Reddit
		if (fullPostType === 'reddit_post') return;
		posts.push({ fullPostType, key: `${fullPostType}-skeleton-${nanoid()}` });
	});
}

function processPlatform(
	platform: PlatformType,
	posts: DisplayablePost[],
	options: {
		validGroupedPosts: PlatformGroupedPosts;
		loadingStates: Record<PlatformType, boolean>;
		retryStates: Record<PlatformType, boolean>;
		recommendationsData: RecommendationsData;
	},
) {
	const { validGroupedPosts, loadingStates, retryStates, recommendationsData } = options;
	const isGroupPlatform = platform === 'facebookgroups' || platform === 'reddit';

	// For group platforms without groups, don't show anything (not even retry)
	if (isGroupPlatform && !recommendationsData.isLoading && !hasRequiredGroups(platform, recommendationsData)) {
		return;
	}

	// Still loading - show skeletons
	if (loadingStates[platform] || (isGroupPlatform && recommendationsData.isLoading)) {
		addSkeletonsForPlatform(platform, posts);
		return;
	}

	// Process each post type individually - show retry for types with invalid content
	const postTypes = platformFullPostTypeMapping[platform];
	const validPosts = validGroupedPosts[platform];

	postTypes.forEach((fullPostType) => {
		// Skip reddit_post - only show reddit_group_post for Reddit
		if (fullPostType === 'reddit_post') return;

		// Find valid post for this type
		const validPost = validPosts.find((post) => getFullPostType(platform, post.postTypeName) === fullPostType);

		if (validPost) {
			// Has valid post - show it
			posts.push({ fullPostType, post: validPost, key: validPost.id });
		} else if (retryStates[platform]) {
			// Terminal state but no valid content for this type - show retry
			posts.push({ fullPostType, needsRetry: true, key: `${fullPostType}-retry-${nanoid()}` });
		}
	});
}

function filterValidPlatformPosts(
	validGroupedPosts: PlatformGroupedPosts,
	selectedPlatform: PlatformType | undefined,
	availablePlatforms: PlatformType[],
	options: {
		loadingStates: Record<PlatformType, boolean>;
		retryStates: Record<PlatformType, boolean>;
		recommendationsData: RecommendationsData;
	},
) {
	const { loadingStates, retryStates, recommendationsData } = options;
	const posts: DisplayablePost[] = [];

	if (selectedPlatform) {
		// When Facebook is selected, include both facebook and facebookgroups
		if (selectedPlatform === 'facebook') {
			processPlatform('facebook', posts, { validGroupedPosts, loadingStates, retryStates, recommendationsData });
			processPlatform('facebookgroups', posts, { validGroupedPosts, loadingStates, retryStates, recommendationsData });
		} else {
			processPlatform(selectedPlatform, posts, { validGroupedPosts, loadingStates, retryStates, recommendationsData });
		}

		return posts;
	}

	// If no platform is selected, return all valid posts and skeletons
	availablePlatforms.forEach((platformName) => {
		processPlatform(platformName, posts, { validGroupedPosts, loadingStates, retryStates, recommendationsData });
		// Include facebookgroups posts when processing facebook platform
		if (platformName === 'facebook') {
			processPlatform('facebookgroups', posts, { validGroupedPosts, loadingStates, retryStates, recommendationsData });
		}
	});

	return posts;
}

function sortFilteredPosts(validPlatformPosts: DisplayablePost[], isMobile: boolean) {
	const weights = isMobile ? mobileFullPostTypeWeights : fullPostTypeWeights;
	// sort the displayable posts by fullPostType weight
	return validPlatformPosts.sort((a, b) => {
		const weightA = weights[a.fullPostType] || 0;
		const weightB = weights[b.fullPostType] || 0;
		return weightB - weightA;
	});
}

type UsePlatformFilterResult = ModelHookResult<
	{
		selectedPlatform: PlatformType | undefined;
		hasAnyValidContent: boolean;
		availablePlatforms: PlatformType[];
		displayablePosts: DisplayablePost[];
		retryablePostTypes: FullPostType[];
	},
	{
		selectPlatform: (platform: PlatformType) => void;
		clearPlatformSelection: () => void;
	}
>;

function validateGroupedPosts(
	groupedPosts: PlatformGroupedPosts,
	loadingStates: Record<PlatformType, boolean>,
	recommendationsData: RecommendationsData,
	petition: StarterDashboardV2PageData['petition'],
) {
	return ALL_AVAILABLE_PLATFORMS.reduce<PlatformGroupedPosts>(
		(acc, platformName) => {
			if (loadingStates[platformName]) return acc;

			const posts = groupedPosts[platformName];

			const validPosts = posts.filter((post) => {
				return hasValidContent(post, post.postTypeName.toLowerCase() === 'story', recommendationsData, petition);
			});

			return {
				...acc,
				[platformName]: validPosts,
			};
		},
		{
			facebook: [],
			instagram: [],
			reddit: [],
			facebookgroups: [],
		},
	);
}

function getInitialPlatform(searchParams: URLSearchParams): PlatformType | undefined {
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-unsafe-type-assertion
	const platformParam = searchParams.get('platform') as PlatformType | undefined;
	return platformParam && ALL_AVAILABLE_PLATFORMS.includes(platformParam) ? platformParam : undefined;
}

function usePlatformStates(platformData: PlatformDataMap) {
	const groupedPosts = useMemo(() => groupPostsByPlatformData(platformData), [platformData]);
	const loadingStates = useMemo(() => groupLoadingStatesByPlatform(platformData), [platformData]);
	const retryStates = useMemo(() => groupRetryStatesByPlatform(platformData), [platformData]);
	return { groupedPosts, loadingStates, retryStates };
}

type UseDisplayablePostsOptions = {
	validGroupedPosts: PlatformGroupedPosts;
	selectedPlatform: PlatformType | undefined;
	availablePlatforms: PlatformType[];
	platformStates: {
		loadingStates: Record<PlatformType, boolean>;
		retryStates: Record<PlatformType, boolean>;
	};
	recommendationsData: RecommendationsData;
	isMobile: boolean;
};

function useDisplayablePosts(options: UseDisplayablePostsOptions) {
	const { validGroupedPosts, selectedPlatform, availablePlatforms, platformStates, recommendationsData, isMobile } =
		options;
	const { loadingStates, retryStates } = platformStates;

	const filtered = useMemo(
		() =>
			filterValidPlatformPosts(validGroupedPosts, selectedPlatform, availablePlatforms, {
				loadingStates,
				retryStates,
				recommendationsData,
			}),
		[validGroupedPosts, selectedPlatform, availablePlatforms, loadingStates, retryStates, recommendationsData],
	);
	const sorted = useMemo(() => sortFilteredPosts(filtered, isMobile), [filtered, isMobile]);
	const retryablePostTypes = useMemo(
		() => sorted.filter((post) => post.needsRetry).map((post) => post.fullPostType),
		[sorted],
	);
	return { displayablePosts: sorted, retryablePostTypes };
}

// eslint-disable-next-line max-lines-per-function
export function usePlatformFilter(
	platformData: PlatformDataMap,
	recommendationsData: RecommendationsData,
	petition: StarterDashboardV2PageData['petition'],
	isMobile: boolean,
): UsePlatformFilterResult {
	// Derive selected platform from URL (single source of truth)
	const [searchParams, setSearchParams] = useSearchParams();
	const selectedPlatform = useMemo(() => getInitialPlatform(searchParams), [searchParams]);

	const { groupedPosts, loadingStates, retryStates } = usePlatformStates(platformData);

	const selectPlatform = (platform: PlatformType) => {
		const newSearchParams = new URLSearchParams(searchParams);
		newSearchParams.set('platform', platform);
		setSearchParams(newSearchParams, { replace: true });
	};

	const clearPlatformSelection = () => {
		const newSearchParams = new URLSearchParams(searchParams);
		newSearchParams.delete('platform');
		setSearchParams(newSearchParams, { replace: true });
	};

	const validGroupedPosts = useMemo(
		() => validateGroupedPosts(groupedPosts, loadingStates, recommendationsData, petition),
		[groupedPosts, loadingStates, recommendationsData, petition],
	);

	// Compute available platforms from ALL valid grouped posts (not filtered by selection)
	const availablePlatforms = useMemo(
		() => computeAvailablePlatforms(validGroupedPosts, loadingStates, retryStates, recommendationsData),
		[loadingStates, retryStates, validGroupedPosts, recommendationsData],
	);

	// Validate selected platform: if it's not available, clear it and show all posts
	useEffect(() => {
		if (selectedPlatform && !availablePlatforms.includes(selectedPlatform)) {
			const newSearchParams = new URLSearchParams(searchParams);
			newSearchParams.delete('platform');
			setSearchParams(newSearchParams, { replace: true });
		}
	}, [selectedPlatform, availablePlatforms, searchParams, setSearchParams]);

	const { displayablePosts, retryablePostTypes } = useDisplayablePosts({
		validGroupedPosts,
		selectedPlatform,
		availablePlatforms,
		platformStates: { loadingStates, retryStates },
		recommendationsData,
		isMobile,
	});

	return {
		data: {
			selectedPlatform,
			hasAnyValidContent: availablePlatforms.length > 0,
			availablePlatforms,
			displayablePosts,
			retryablePostTypes,
		},
		actions: {
			selectPlatform,
			clearPlatformSelection,
		},
	};
}
